import { ASSIGNMENT_TYPE, RESERVATION_LABEL } from '@/types/capacityCalendar';
import type { AssignmentType, CalendarRow, CapacityAllocationMap } from '@/types/capacityCalendar';

export interface SchedulePortion {
  rowId: string;
  assignmentId: string;
  capacityTypeId: string;
  typeCode: string;
  quantity: number;
}

export interface ScheduleGroup {
  key: string;
  type: AssignmentType;
  poNumber: string | null;
  notes: string | null;
  // Non-null means the PO has no Finished Goods lines, so it sits in the week for planning
  // visibility and must never consume capacity — the board needs it to refuse a quantity edit
  // rather than discover the rule from a rejected write.
  productCategory: string | null;
  totalQuantity: number;
  portions: SchedulePortion[];
}

export interface GroupBreakdownRow {
  typeCode: string;
  quantity: number;
}

export interface VendorWeekCell {
  groups: ScheduleGroup[];
  scheduledTotal: number;
  remainingTotal: number;
  overbookedTypes: { typeCode: string; by: number }[];
  hasPlan: boolean;
}

export interface VendorLane {
  vendorId: string | undefined;
  vendorCode: string;
  vendorName: string;
  typeCodes: string[];
  cells: Record<string, VendorWeekCell>;
}

// The board's two kinds of row: one Scheduled row of PO blocks on top, then a read-only
// capacity strip per type below it.
export interface ScheduledBoardRow {
  kind: 'scheduled';
  lane: VendorLane;
}

export interface TypeBoardRow {
  kind: 'type';
  lane: VendorLane;
  calendarRow: CalendarRow;
}

export type BoardRow = ScheduledBoardRow | TypeBoardRow;

// A block, located: which vendor lane and week it sits in. Enough to act on it once the
// menu that asked has closed.
export interface BlockSelection {
  lane: VendorLane;
  weekKey: string;
  group: ScheduleGroup;
}

// Groups the calendar's per-type PO assignments into per-PO blocks for the scheduling
// board. Rows sharing a distributionGroupId are one block; a null group id is a block
// of one (single-portion distributions are deliberately ungrouped on the backend).
// Non-ghost calendar entries always carry capacityTypeId; the filter enforces this invariant.
export function buildScheduleBoard(rows: CalendarRow[], allocations: CapacityAllocationMap): VendorLane[] {
  const lanes: VendorLane[] = [];
  const laneByVendorCode = new Map<string, VendorLane>();

  rows
    .filter((row) => !row.isGhost && row.capacityTypeId)
    .forEach((row) => {
      const capacityTypeId = row.capacityTypeId as string; // Narrowed by filter above
      let lane = laneByVendorCode.get(row.vendorCode);
      if (!lane) {
        lane = { vendorId: row.vendorId, vendorCode: row.vendorCode, vendorName: row.vendorName, typeCodes: [], cells: {} };
        laneByVendorCode.set(row.vendorCode, lane);
        lanes.push(lane);
      }
      lane.typeCodes.push(row.typeCode);

      Object.entries(allocations[row.id] ?? {}).forEach(([weekKey, allocation]) => {
        const cell = (lane.cells[weekKey] ??= { groups: [], scheduledTotal: 0, remainingTotal: 0, overbookedTypes: [], hasPlan: false });
        cell.hasPlan = true;
        if (allocation.remainedCapacity >= 0) {
          cell.remainingTotal += allocation.remainedCapacity;
        } else {
          cell.overbookedTypes.push({ typeCode: row.typeCode, by: -allocation.remainedCapacity });
        }

        allocation.purchaseOrders.forEach((po, index) => {
          const assignmentId = po.id ?? `${row.id}|${weekKey}|${index}`;
          const key = po.distributionGroupId ?? assignmentId;
          let group = cell.groups.find((candidate) => candidate.key === key);
          if (!group) {
            group = {
              key,
              type: po.type ?? ASSIGNMENT_TYPE.po,
              poNumber: po.poNumber,
              notes: po.notes ?? null,
              productCategory: po.productCategory ?? null,
              totalQuantity: 0,
              portions: [],
            };
            cell.groups.push(group);
          }
          group.totalQuantity += po.quantity;
          cell.scheduledTotal += po.quantity;
          group.portions.push({ rowId: row.id, assignmentId, capacityTypeId, typeCode: row.typeCode, quantity: po.quantity });
        });
      });
    });

  return lanes;
}

// The breakdown lists every capacity type the vendor has, so a type this block does
// not consume reads as an explicit 0 instead of dropping out of the list entirely.
// A portion on a type the lane no longer lists is appended rather than swallowed.
export function groupBreakdown(group: ScheduleGroup, typeCodes: string[]): GroupBreakdownRow[] {
  const quantityByType = new Map<string, number>();
  typeCodes.forEach((typeCode) => quantityByType.set(typeCode, 0));
  group.portions.forEach((portion) => {
    quantityByType.set(portion.typeCode, (quantityByType.get(portion.typeCode) ?? 0) + portion.quantity);
  });
  return Array.from(quantityByType, ([typeCode, quantity]) => ({ typeCode, quantity }));
}

// The identity a block is ordered, hovered and selected by — the same value its capacity
// segments carry (see assignmentOrderKey), so an interaction with either lights up the other.
// A manual block goes by its group, never by a PO number it may carry: that number is a
// tracking reference, and the segments below it are keyed by the group.
export function groupBlockKey(group: ScheduleGroup): string {
  return group.type === ASSIGNMENT_TYPE.manual ? group.key : (group.poNumber ?? group.key);
}

// What a block is called in the Scheduled row, where the cards have to be told apart at a
// glance: a reservation by its comment, since nothing else distinguishes two of them,
// falling back to a PO number it may carry for tracking. Mirrors assignmentLabel, which names
// the same block's capacity segments. Callers truncate; groupTitle has the full-view heading.
export function groupLabel(group: ScheduleGroup): string {
  if (group.type !== ASSIGNMENT_TYPE.manual) {
    return group.poNumber ?? RESERVATION_LABEL;
  }
  return group.notes?.trim() || group.poNumber || RESERVATION_LABEL;
}


// Where a block lives on the board, by the same key the board selects it with (groupBlockKey).
// A PO split across weeks has one card per week; the earliest is the anchor, since that is
// where the reader's eye starts and the rest are tinted as its family anyway.
export function locateBlock(lanes: VendorLane[], blockKey: string): BlockSelection | null {
  return lanes
    .flatMap((lane) =>
      Object.entries(lane.cells).flatMap(([weekKey, cell]) =>
        cell.groups.filter((group) => groupBlockKey(group) === blockKey).map<BlockSelection>((group) => ({ lane, weekKey, group })),
      ),
    )
    .reduce<BlockSelection | null>((earliest, found) => (earliest === null || found.weekKey < earliest.weekKey ? found : earliest), null);
}

// Every block whose PO number contains the query, in the order the board reads: vendor tab
// order, then week. A reservation matches on the tracking PO number its card shows, never on
// its note — the search is over POs, and a note mentioning one is not a block of it. A blank
// query is no query, not a match for everything.
export function searchBlocks(lanes: VendorLane[], query: string): BlockSelection[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  return lanes.flatMap((lane) =>
    Object.entries(lane.cells)
      .sort(([weekA], [weekB]) => weekA.localeCompare(weekB))
      .flatMap(([weekKey, cell]) =>
        cell.groups.filter((group) => group.poNumber?.toLowerCase().includes(needle)).map<BlockSelection>((group) => ({ lane, weekKey, group })),
      ),
  );
}

// The one PO a search has narrowed to, if it has: every hit carries the same number, and the
// first is its earliest card on the first vendor that holds it — where locateBlock would land.
// While hits still span several POs there is nothing to reveal yet, and the board must not
// jump around under a half-typed query.
export function singlePoHit(hits: BlockSelection[]): BlockSelection | null {
  const [first] = hits;
  if (!first) return null;
  return hits.every((hit) => hit.group.poNumber === first.group.poNumber) ? first : null;
}

// The block one of whose portions is this assignment — the exact card an activity event
// touched, where locateBlock can only name the PO's family.
export function locateBlockByAssignment(lanes: VendorLane[], assignmentId: string): BlockSelection | null {
  return (
    lanes
      .flatMap((lane) =>
        Object.entries(lane.cells).flatMap(([weekKey, cell]) =>
          cell.groups
            .filter((group) => group.portions.some((portion) => portion.assignmentId === assignmentId))
            .map<BlockSelection>((group) => ({ lane, weekKey, group })),
        ),
      )
      .at(0) ?? null
  );
}

// The block order the Scheduled row shows, expressed in the keys an allocation bar orders
// its segments by — so the nth card above is the nth segment in every capacity row below.
export function cellOrderKeys(cell: VendorWeekCell | undefined): string[] {
  return (cell?.groups ?? []).map(groupBlockKey);
}

// A reservation is already labelled with its own note (groupLabel), so a marker beside it
// would say the same thing twice. A PO card is labelled with its number, which leaves the
// note invisible unless the card carries one.
export function groupNoteNeedsMarker(group: ScheduleGroup): boolean {
  return group.type !== ASSIGNMENT_TYPE.manual && Boolean(group.notes?.trim());
}

// Line items are a property of the purchase order behind a block. A manual block has none
// even when it carries a PO number — that number is a tracking reference, not a commitment
// to an order whose lines could be read.
export function groupHasLineItems(group: ScheduleGroup): boolean {
  return group.type !== ASSIGNMENT_TYPE.manual && Boolean(group.poNumber);
}

// Mirrors the "View in Capacity Calendar" deep link's contract: the allocation
// param locates the row/week cell, and either `po` or `manual` identifies which
// piece of it to highlight. Uses the group's first portion, matching how the
// board only ever links to the row that first portion lives on.
export function calendarLinkQuery(group: ScheduleGroup, weekKey: string): Record<string, string> {
  const [portion] = group.portions;
  const allocation = `${portion.rowId}|${weekKey}`;
  return group.type === ASSIGNMENT_TYPE.manual ? { allocation, manual: portion.assignmentId } : { allocation, po: group.poNumber ?? '' };
}

// The activity feed identifies a PO block by its number. A reservation has none to give, so
// it is identified by the first portion's assignment id — the API returns every event whose
// payload lists that id, which is every event of the whole block.
export function activityLinkQuery(group: ScheduleGroup): Record<string, string> {
  const [portion] = group.portions;
  return group.type === ASSIGNMENT_TYPE.manual || !group.poNumber ? { assignmentId: portion.assignmentId } : { po: group.poNumber };
}

// A negative remainder means the week is already overbooked; naming that beats printing
// "-3,440 left", where the reader has to spot a minus sign to see there is a problem.
export function formatRemaining(value: number): string {
  return value < 0 ? `${Math.abs(value).toLocaleString()} overbooked` : `${value.toLocaleString()} left`;
}
